import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { istZahlMitEinheit } from "@/office/excel/formeln";
import { istTextformat } from "@/office/excel/zahlenformat";
import { filledCells, isNumber, isText, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

export const lektionWerttypen: Lesson = {
  id: "werttypen",
  title: "Zahl oder Text? Excel entscheidet beim Tippen",
  summary: "Warum „1.200 €“ und 1200 zwei völlig verschiedene Dinge sind – und wie man den Unterschied sieht.",
  durationMinutes: 11,
  learningGoals: [
    "An der Ausrichtung erkennen, ob Excel einen Wert als Zahl gelesen hat",
    "Einheiten ins Zahlenformat legen statt in den Wert",
    "Als Text gespeicherte Zahlen erkennen und reparieren",
  ],
  steps: [
    {
      id: "ausrichtung-verraet",
      title: "Die Ausrichtung verrät alles",
      explanation: [
        "Wenn du etwas in eine Zelle tippst, trifft Excel sofort eine Entscheidung: " +
          "Ist das eine Zahl, ein Datum oder Text? Diese Entscheidung ist folgenreich, " +
          "denn nur mit Zahlen kann Excel rechnen. Und sie ist unsichtbar – jedenfalls " +
          "auf den ersten Blick.",
        "Auf den zweiten Blick ist sie offensichtlich: Zahlen und Datumswerte stehen " +
          "standardmäßig rechtsbündig in der Zelle, Text linksbündig. Das ist kein " +
          "Schönheitsentscheid, sondern eine Statusanzeige. Steht deine Menge " +
          "linksbündig, hat Excel sie als Text gelesen, und jede Summe darüber ergibt 0.",
        "Der häufigste Grund dafür ist eine mitgetippte Einheit: „1200 €“, „15 kg“, " +
          "„ca. 300“. Sobald in der Eingabe etwas steht, das keine Zahl sein kann, wird " +
          "das Ganze zu Text. Ebenso häufig: aus dem Web oder aus einer PDF kopierte " +
          "Zahlen, die ein geschütztes Leerzeichen als Tausendertrennung mitbringen.",
        "Vorsicht ist auch bei der ausdrücklichen Textformatierung geboten. Ist eine " +
          "Zelle als „Text“ formatiert, bleibt selbst eine sauber getippte 1200 Text – " +
          "das Format wirkt hier ausnahmsweise auf den Wert selbst, weil Excel die " +
          "Umwandlung gar nicht erst versucht.",
      ],
      keyPoints: [
        "Rechtsbündig = Zahl oder Datum, linksbündig = Text. Ohne eigenes Zutun.",
        "Eine mitgetippte Einheit macht aus einer Zahl Text.",
        "Eine als „Text“ formatierte Zelle nimmt gar keine Zahl mehr an.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in deiner Liste die Zahlenspalten – etwa Menge und Preis – " +
          "echte Zahlen enthalten. Keine Zelle soll eine Zahl mit angehängter Einheit " +
          "als Text tragen.",
        hint: "Prüfe die Ausrichtung: Was linksbündig steht, ist Text. Einheit entfernen und die reine Zahl eintippen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Werttyp einer Zelle",
              "1.4",
              "Sieh dir die Ausrichtung an: Zahlen stehen rechtsbündig, Text linksbündig.",
            );
          }

          const zellen = filledCells(mappe);
          if (zellen.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste mit ein paar Zahlen ein.");
          }

          const verdaechtig = zellen.filter(
            (zelle) => isText(zelle) && istZahlMitEinheit(String(zelle.value)),
          );

          if (verdaechtig.length > 0) {
            const erste = verdaechtig[0];
            return fail(
              `In Zelle ${erste.address} steht „${erste.value}“ als Text – eine Zahl mit ` +
                `angehängter Einheit.${verdaechtig.length > 1 ? ` Insgesamt betrifft das ${verdaechtig.length} Zellen.` : ""}`,
              "Trage nur die reine Zahl ein. Die Einheit kommt im nächsten Schritt ins Zahlenformat, " +
                "wo sie sichtbar bleibt, ohne das Rechnen zu verhindern.",
            );
          }

          const zahlen = zellen.filter(isNumber);
          if (zahlen.length < 4) {
            return fail(
              `Es stehen erst ${zahlen.length} echte Zahlen in der Arbeitsmappe.`,
              "Ergänze deine Liste um Zahlenspalten, etwa Menge und Preis, mit mindestens vier Werten.",
            );
          }

          return pass(
            `${zahlen.length} Zellen enthalten echte Zahlen, keine davon mit angehängter Einheit. ` +
              "Damit lässt sich rechnen.",
          );
        },
      },
    },
    {
      id: "textformat-falle",
      title: "Die Textformat-Falle",
      explanation: [
        "Eine Zelle, die als „Text“ formatiert ist, nimmt keine Zahlen mehr an – auch " +
          "keine korrekt getippten. Das ist selten Absicht und passiert meist beim " +
          "Import: Wer eine CSV-Datei öffnet und dabei alle Spalten auf „Text“ stellt, " +
          "um führende Nullen bei Postleitzahlen zu retten, macht damit auch alle " +
          "Beträge unbrauchbar.",
        "Das Tückische: Ändert man das Format nachträglich auf „Zahl“, passiert erst " +
          "einmal nichts. Der Inhalt bleibt Text, weil Excel nur neu eingegebene Werte " +
          "prüft. Es braucht einen zweiten Schritt, der die Zellen neu bewerten lässt.",
        "Der schnellste Weg dafür: eine leere Zelle kopieren, den betroffenen Bereich " +
          "markieren, „Inhalte einfügen“ mit der Rechenart „Addieren“. Excel rechnet " +
          "dann null dazu – und weil es dafür rechnen muss, wandelt es jeden Text, der " +
          "eine Zahl sein könnte, in eine Zahl um. Alternativ zeigt Excel bei solchen " +
          "Zellen ein kleines grünes Dreieck und bietet über das Warndreieck " +
          "„In eine Zahl umwandeln“ an.",
      ],
      keyPoints: [
        "Format nachträglich ändern reicht nicht – der Inhalt bleibt Text.",
        "Leere Zelle kopieren, dann Inhalte einfügen → Addieren wandelt alles um.",
        "Ein grünes Dreieck links oben in der Zelle ist Excels Hinweis darauf.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass keine Zelle deiner Zahlenspalten ausdrücklich als „Text“ " +
          "formatiert ist.",
        hint: "Spalte markieren, Start → Zahlenformat auf „Standard“ oder „Zahl“ stellen – und danach die Werte neu bewerten lassen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Das Zahlenformat einer Zelle",
              "1.4",
              "Markiere deine Zahlenspalten und sieh im Feld „Zahlenformat“ nach, was dort steht.",
            );
          }

          const zellen = filledCells(mappe);
          if (zellen.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          // Als Text formatiert und dabei etwas enthaltend, das eine Zahl sein könnte.
          const betroffen = zellen.filter(
            (zelle) => istTextformat(zelle) && /^\s*[+-]?[\d.,\s]+\s*$/.test(String(zelle.value)),
          );

          if (betroffen.length === 0) {
            return pass(
              "Keine Zahlenzelle ist als „Text“ formatiert – Excel kann mit allen Werten rechnen.",
            );
          }

          const erste = betroffen[0];
          return fail(
            `Zelle ${erste.address} enthält „${erste.value}“, ist aber als „Text“ formatiert. ` +
              `${betroffen.length > 1 ? `Insgesamt sind ${betroffen.length} Zellen betroffen.` : "Excel rechnet damit nicht."}`,
            "Format auf „Standard“ stellen, dann eine leere Zelle kopieren und über " +
              "„Inhalte einfügen“ → „Addieren“ auf den Bereich anwenden.",
          );
        },
      },
    },
  ],
};
